import { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiCallsChart } from "./charts/ApiCallsChart";
import { TokenUsageChart } from "./charts/TokenUsageChart";
import { CostChart } from "./charts/CostChart";
import { AllApiCalls } from "./AllApiCalls";
import { StatsCards } from "./cards/StatsCards";
import { PlanUsageCards } from "./cards/PlanUsageCards";
import { StatsFilter } from "./filters/StatsFilter";
import { DashboardUnifiedFiltersCard } from "./filters/DashboardUnifiedFilters";
import { ClientConsumptionTable } from "./dashboard/ClientConsumptionTable";
import { ModelConsumptionTable } from "./dashboard/ModelConsumptionTable";
import { EtegHealthIndicators } from "./dashboard/EtegHealthIndicators";
import {
  createDefaultDashboardFilters,
  type DashboardUnifiedFilters,
} from "@/types/dashboardFilters";
import { PermissionsDebug } from "./PermissionsDebug";
import { Building2, Briefcase } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Footer } from "./Footer";
import { mockClients, mockWorkspaces } from "@/data/mockData";

interface DashboardProps {
  viewType: "admin" | "client";
}

export const Dashboard = ({ viewType }: DashboardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState<string>("all");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("all");
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [filterClientId, setFilterClientId] = useState<string | null>(null);
  const [etegFilters, setEtegFilters] = useState<DashboardUnifiedFilters>(
    () => createDefaultDashboardFilters(),
  );

  const historyPeriodStart = useMemo(
    () => new Date(selectedYear, selectedMonth - 1, 1),
    [selectedYear, selectedMonth],
  );
  const historyPeriodEnd = useMemo(
    () => new Date(selectedYear, selectedMonth, 0, 23, 59, 59, 999),
    [selectedYear, selectedMonth],
  );

  const etegMonth = etegFilters.startDate.getMonth() + 1;
  const etegYear = etegFilters.startDate.getFullYear();

  const etegHistoryFilters = useMemo(
    () => ({
      client: etegFilters.client,
      type: etegFilters.type,
      status: etegFilters.status,
      product: etegFilters.product,
      startDate: etegFilters.startDate,
      endDate: etegFilters.endDate,
      search: etegFilters.search,
    }),
    [etegFilters],
  );

  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string } | null)?.scrollTo;
    if (!scrollTo) return;

    const timer = window.setTimeout(() => {
      document
        .getElementById(scrollTo)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      navigate(location.pathname, { replace: true, state: null });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [location.state, location.pathname, navigate]);

  // Mock: Get user's accessible clients (this would come from user's permissions)
  const getUserAccessibleClients = () => {
    // For demonstration, assuming current user has access to first 2 clients
    return mockClients.slice(0, 2);
  };

  const accessibleClients = getUserAccessibleClients();

  // Get workspaces for selected client
  const getWorkspacesForClient = (clientId: string) => {
    if (clientId === "all")
      return mockWorkspaces.filter((ws) =>
        accessibleClients.some((c) => c.id === ws.clientId)
      );
    return mockWorkspaces.filter((ws) => ws.clientId === clientId);
  };

  const availableWorkspaces = getWorkspacesForClient(selectedClientId);

  // Handle client selection
  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);

    if (clientId === "all") {
      setSelectedWorkspaceId("all");
    } else {
      const clientWorkspaces = getWorkspacesForClient(clientId);
      if (clientWorkspaces.length === 1) {
        // Auto-select the only workspace
        setSelectedWorkspaceId(clientWorkspaces[0].id);
      } else {
        // Reset to "all workspaces"
        setSelectedWorkspaceId("all");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted pb-12">
      {/* Main Content */}
      <main className="container mx-auto p-6 space-y-8">
        {/* Debug de Permissões - apenas em desenvolvimento */}
        <PermissionsDebug />

        {/* Client and Workspace Selection for Client View */}
        {viewType === "client" && (
          <Card className="bg-card/50 border-primary/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Seleção de Cliente e Workspace
              </CardTitle>
              <CardDescription>
                Selecione o cliente e workspace para visualizar os dados
                específicos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Client Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Cliente
                  </label>
                  <Select
                    value={selectedClientId}
                    onValueChange={handleClientChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecione um cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-primary" />
                          Todos os Clientes
                        </div>
                      </SelectItem>
                      {accessibleClients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-primary" />
                            {client.company}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Workspace Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    Workspace
                  </label>
                  <Select
                    value={selectedWorkspaceId}
                    onValueChange={setSelectedWorkspaceId}
                    disabled={
                      selectedClientId !== "all" &&
                      availableWorkspaces.length === 1
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecione um workspace" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedClientId === "all" ||
                      availableWorkspaces.length > 1 ? (
                        <SelectItem value="all">
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-primary" />
                            Todos os Workspaces
                          </div>
                        </SelectItem>
                      ) : null}
                      {availableWorkspaces.map((workspace) => (
                        <SelectItem key={workspace.id} value={workspace.id}>
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-primary" />
                            {workspace.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedClientId && selectedWorkspaceId && (
                <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/10">
                  <div className="flex items-center gap-2 text-sm">
                    <Badge
                      variant="secondary"
                      className="bg-primary/10 text-primary"
                    >
                      {selectedClientId === "all"
                        ? "Todos os Clientes"
                        : accessibleClients.find(
                            (c) => c.id === selectedClientId
                          )?.company}
                    </Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant="outline" className="border-primary/20">
                      {selectedWorkspaceId === "all"
                        ? "Todos os Workspaces"
                        : availableWorkspaces.find(
                            (w) => w.id === selectedWorkspaceId
                          )?.name}
                    </Badge>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Filter */}
        {viewType === "client" && (
          <StatsFilter
            onFilterChange={(filters) => {
              if (filters.startDate && filters.endDate) {
                setSelectedMonth(filters.startDate.getMonth() + 1);
                setSelectedYear(filters.startDate.getFullYear());
              }
              if (filters.clientes && filters.clientes.length > 0) {
                if (filters.clientes.includes("todos")) {
                  setFilterClientId(null);
                } else if (filters.clientes.includes("sandbox")) {
                  setFilterClientId("null");
                } else if (filters.clientes.length === 1) {
                  setFilterClientId(filters.clientes[0]);
                } else {
                  setFilterClientId(filters.clientes[0]);
                }
              } else {
                setFilterClientId(null);
              }
            }}
          />
        )}

        {viewType === "admin" ? (
          <>
            <DashboardUnifiedFiltersCard
              value={etegFilters}
              onApply={setEtegFilters}
            />

            <ClientConsumptionTable
              month={etegMonth}
              year={etegYear}
              clientId={etegFilters.client}
              search={etegFilters.search}
            />

            <ModelConsumptionTable
              startDate={etegFilters.startDate}
              endDate={etegFilters.endDate}
              clientId={etegFilters.client}
              productId={etegFilters.product}
            />

            <EtegHealthIndicators
              startDate={etegFilters.startDate}
              endDate={etegFilters.endDate}
              month={etegMonth}
              year={etegYear}
              clientId={etegFilters.client}
              productId={etegFilters.product}
            />

            <AllApiCalls
              variant="embedded"
              hideFilters
              controlledFilters={etegHistoryFilters}
              startDate={etegFilters.startDate}
              endDate={etegFilters.endDate}
            />
          </>
        ) : (
          <>
            <div className="space-y-4">
              <StatsCards
                clientId={filterClientId || selectedClientId}
                workspaceId={selectedWorkspaceId}
                month={selectedMonth}
                year={selectedYear}
              />
              <PlanUsageCards
                month={selectedMonth}
                year={selectedYear}
                clientId={filterClientId || selectedClientId}
              />
            </div>

            <CostChart
              clientId={filterClientId || selectedClientId}
              workspaceId={selectedWorkspaceId}
            />

            <div className="grid gap-6 md:grid-cols-2">
              <ApiCallsChart
                clientId={filterClientId || selectedClientId}
                workspaceId={selectedWorkspaceId}
              />
              <TokenUsageChart
                clientId={filterClientId || selectedClientId}
                workspaceId={selectedWorkspaceId}
              />
            </div>

            <AllApiCalls
              variant="embedded"
              clientId={filterClientId || selectedClientId}
              startDate={historyPeriodStart}
              endDate={historyPeriodEnd}
            />
          </>
        )}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};
